import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Button, Spinner } from '../common';
import { useTranslationProjects } from '../../hooks/useTranslationProjects';

interface Props {
  onAdd: () => void;
}

// The "Vertimai" tab of My Books: a list of the user's translation projects,
// each linking into the reader. Empty state offers the same add action.
export default function TranslationsTab({ onAdd }: Props) {
  const { t } = useTranslation();
  const { data: projects, isLoading } = useTranslationProjects();

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!projects || projects.length === 0) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="py-16 text-center">
        <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-gray-100">
          {t('books.noTranslations')}
        </h2>
        <Button variant="accent" size="lg" onClick={onAdd}>
          {t('books.addTranslation')}
        </Button>
      </motion.div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {projects.map((p) => {
        const pct = p.segmentCount > 0 ? Math.round((p.translatedCount / p.segmentCount) * 100) : 0;
        return (
          <Link
            key={p.id}
            to={`/translate/${p.id}`}
            className="card-hover flex items-center justify-between gap-4 p-4"
          >
            <div className="min-w-0">
              <h3 className="truncate text-base font-semibold text-gray-900 dark:text-gray-100">
                {p.title}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {p.sourceLanguage} → {p.targetLanguage}
                {p.status !== 'ready' && <em className="ml-2">({t(`statuses.${p.status}`, p.status)})</em>}
                {p.conflictCount > 0 && (
                  <span className="ml-2 rounded bg-amber-100 px-1.5 text-amber-700">{p.conflictCount} ⚠</span>
                )}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <div className="h-1.5 w-24 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                <div className="h-full bg-primary-500" style={{ width: `${pct}%` }} />
              </div>
              <span className="w-20 text-right text-xs text-gray-500 dark:text-gray-400">
                {p.translatedCount}/{p.segmentCount}
              </span>
            </div>
          </Link>
        );
      })}
    </div>
  );
}
